<script setup lang="ts">
//#region imports
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import _ from '@/setup/lodash';
import { useApi } from '@/api';
import { useTableSearch, useTableSort } from '@/composables/useTableLS';
import { isErrorResponse, type EmitsToListeners, type FormErrors } from '@/types/core';
import type { UiTableNewField, UiTableNewSortEmit } from '@/types/table';
import type {
  GetAdminsByIdResponse,
  GetAdminsData,
  GetAdminsResponse,
  GetTeamsData,
  GetTeamsResponse,
} from '@/types/openapi-ts';
//#endregion

//#region types
type Admin = GetAdminsResponse['payload'][number];
type UserInfo = GetAdminsByIdResponse['payload'];
type PopupUserInfo = Pick<UserInfo, 'id' | 'name' | 'teams'>;
type Team = GetTeamsResponse['payload'][number];
type AdminQuery = NonNullable<GetAdminsData['query']>;
type TeamQuery = NonNullable<GetTeamsData['query']>;
type TableRow = Admin | Team;

type AttachContext =
  | {
      mode: 'select-team';
      userInfo: PopupUserInfo;
    }
  | {
      mode: 'select-user';
      team: Partial<Team>;
    };

interface Props {
  context: AttachContext;
}

interface Emits {
  refresh: [];
  close: [];
}

export interface AttachUserToTeamParams {
  data: Props;
  listeners?: EmitsToListeners<Emits>;
}
//#endregion

//#region define
const props = defineProps<Props>();
const emit = defineEmits<Emits>();
//#endregion

//#region const
const LS_PATH = 'AttachUserToTeam';

const sortFields: UiTableNewField<TableRow>[] = [
  {
    name: 'name',
    sortable: 'custom',
  },
  {
    name: 'email',
    sortable: 'custom',
  },
  {
    name: 'members_count',
    sortable: 'custom',
  },
];
//#endregion

//#region composables
const api = useApi();
const { t } = useI18n();
const { searchQuery } = useTableSearch();
const { sortProp, sortOrder, optionsSort } = useTableSort({
  initialOptions: {
    sort_column: 'name',
    sort_dir: 'asc',
  },
  lsPath: LS_PATH,
  fields: sortFields,
});
//#endregion

//#region state
const admins = ref<Admin[]>([]);
const isDataLoading = ref(false);
const isSaving = ref(false);
const isVisible = ref(false);
const selectedId = ref<string | null>(null);
const teams = ref<Team[]>([]);
const errors = ref<FormErrors>({});
//#endregion

//#region computed
const fields = computed<UiTableNewField<TableRow>[]>(() => [
  {
    name: 'name',
    sortable: 'custom',
  },
  {
    name: props.context.mode === 'select-team' ? 'members_count' : 'email',
    sortable: 'custom',
  },
]);

const tableData = computed(() => {
  if (props.context.mode === 'select-team') {
    return _.differenceBy(teams.value, props.context.userInfo.teams ?? [], 'id');
  }

  const teamId = props.context.team.id;
  if (!teamId) {
    return [];
  }

  return admins.value.filter((admin) => (
    !admin.teams?.some((team) => team.id === teamId)
  ));
});

const title = computed(() => (
  props.context.mode === 'select-team'
    ? t('team_popup.add_user_in_teams_title', { name: props.context.userInfo.name })
    : t('team_popup.add_user_in_team_title', { teamName: props.context.team.name })
));

const submitTitle = computed(() => (
  props.context.mode === 'select-team'
    ? t('team_popup.add_team_button')
    : t('team_popup.add_user_button')
));
//#endregion

//#region methods
const handleSort = ({ prop, order }: UiTableNewSortEmit) => {
  if (order === '') return;
  if (sortProp.value !== prop) {
    sortProp.value = prop;
  } else {
    sortOrder.value = order;
  }
  nextTick(() => {
    getData();
  });
};

function open() {
  isVisible.value = true;
  getData();
}

function handleCheckedRows(_id: string, ids: string[]) {
  selectedId.value = ids[0] ?? null;
}

function setErrors(error: unknown) {
  if (!isErrorResponse(error)) {
    throw error;
  }

  errors.value = _.cloneDeep(error.data.errors) ?? {};
}

function getData() {
  isDataLoading.value = true;
  errors.value = {};

  const request = props.context.mode === 'select-team'
    ? getTeams()
    : getAdmins();

  request.catch(setErrors).finally(() => {
    isDataLoading.value = false;
  });
}

function getTeams() {
  const sortColumn = optionsSort.value.sort_column === 'members_count'
    ? 'members_count'
    : 'name';
  const query = {
    search: searchQuery.value,
    sort_column: sortColumn,
    sort_dir: optionsSort.value.sort_dir,
  } as TeamQuery;

  return api.getTeamsList(query).then((response) => {
    teams.value = response.data.payload;
  });
}

function getAdmins() {
  const sortColumn = optionsSort.value.sort_column === 'email' ? 'email' : 'name';
  const query = {
    search: searchQuery.value,
    sort_column: sortColumn,
    sort_dir: optionsSort.value.sort_dir,
  } as AdminQuery;

  return api.getAdmins(query).then((response) => {
    admins.value = response.data.payload;
  });
}

function handleClose() {
  emit('close');
  isVisible.value = false;
}

function save() {
  if (!selectedId.value) {
    return;
  }

  const adminId = props.context.mode === 'select-team'
    ? props.context.userInfo.id
    : selectedId.value;
  const teamId = props.context.mode === 'select-team'
    ? selectedId.value
    : props.context.team.id;

  if (!adminId || !teamId) {
    return;
  }

  isSaving.value = true;
  errors.value = {};

  api.postAttachToTeam(adminId, teamId).then(() => {
    emit('refresh');
    handleClose();
  }).catch(setErrors).finally(() => {
    isSaving.value = false;
  });
}
//#endregion

defineExpose({
  open,
  handleClose,
});
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    :title="title"
    :width="800"
    @close="handleClose"
  >
    <template v-slot:body>
      <div class="t:space-y-16">
        <UiInput
          v-model.trim="searchQuery"
          is-search
          autosize
          @search="getData"
        />
        <UiTableNew
          v-loading="isDataLoading"
          select-row
          is-static
          :fields="fields"
          :data="tableData"
          :padding="0"
          :sort="{
            prop: sortProp,
            order: sortOrder,
          }"
          i18n-path="team_users.list"
          :show-check-all="false"
          select-type="radio"
          checked-row
          checked-row-id="id"
          element-loading-custom-class="data-loader-spinner"
          class="t:overflow-y-hidden"
          @sort="handleSort"
          @checked-rows="handleCheckedRows"
        />
      </div>
    </template>
    <template v-slot:footer>
      <div class="t:flex t:justify-end t:space-x-10">
        <UiButton
          type="negative"
          size="big"
          icon="times"
          @click="handleClose"
        >
          {{ t('team_popup.cancel_button') }}
        </UiButton>
        <UiButton
          :disabled="!selectedId"
          :loading="isSaving"
          filled
          size="big"
          icon="plus"
          @click="save"
        >
          {{ submitTitle }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>
